"use client";

import { motion } from "motion/react";
import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

type Stat = {
  value: string | number;
  suffix?: string;
  prefix?: string;
  label: string;
  description?: string;
  accent?: "red" | "coral" | "purple" | "cyan" | "denim" | "emerald" | "lime" | "amber-gold" | "crimson";
  icon?: React.ReactNode;
};

const ACCENT_BG: Record<NonNullable<Stat["accent"]>, string> = {
  red: "from-red-500/20 to-red-500/0",
  coral: "from-coral-500/20 to-coral-500/0",
  purple: "from-purple-500/20 to-purple-500/0",
  cyan: "from-cyan-500/20 to-cyan-500/0",
  denim: "from-denim-500/20 to-denim-500/0",
  emerald: "from-emerald-500/20 to-emerald-500/0",
  lime: "from-lime-500/20 to-lime-500/0",
  "amber-gold": "from-amber-gold-500/20 to-amber-gold-500/0",
  crimson: "from-crimson-500/20 to-crimson-500/0",
};

const ACCENT_TEXT: Record<NonNullable<Stat["accent"]>, string> = {
  red: "text-red-500",
  coral: "text-coral-500",
  purple: "text-purple-500",
  cyan: "text-cyan-500",
  denim: "text-denim-500",
  emerald: "text-emerald-500",
  lime: "text-lime-500",
  "amber-gold": "text-amber-gold-500",
  crimson: "text-crimson-500",
};

const ACCENT_BORDER: Record<NonNullable<Stat["accent"]>, string> = {
  red: "border-red-500/30",
  coral: "border-coral-500/30",
  purple: "border-purple-500/30",
  cyan: "border-cyan-500/30",
  denim: "border-denim-500/30",
  emerald: "border-emerald-500/30",
  lime: "border-lime-500/30",
  "amber-gold": "border-amber-gold-500/30",
  crimson: "border-crimson-500/30",
};

function Counter({ value, prefix = "", suffix = "" }: { value: number; prefix?: string; suffix?: string }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1500;
          const start = performance.now();
          const tick = (now: number) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setDisplay(Math.round(eased * value));
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  return (
    <span ref={ref}>
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>
  );
}

export function CounterBlock({ stats, columns = 4 }: { stats: Stat[]; columns?: 2 | 3 | 4 | 5 | 6 }) {
  const colClass = {
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-2 lg:grid-cols-4",
    5: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5",
    6: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6",
  }[columns];

  return (
    <div className={cn("grid gap-4", colClass)}>
      {stats.map((stat, i) => {
        const accent = stat.accent ?? "purple";
        const numericValue = typeof stat.value === "number" ? stat.value : parseFloat(String(stat.value).replace(/[^0-9.]/g, "")) || 0;
        const isNumeric = !isNaN(numericValue) && numericValue > 0;
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className={cn(
              "glass-card relative overflow-hidden rounded-2xl border p-6",
              ACCENT_BORDER[accent]
            )}
          >
            <div className={cn("absolute inset-0 bg-gradient-to-br", ACCENT_BG[accent])} />
            <div className="relative">
              {stat.icon && (
                <div className={cn("mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-muted/50", ACCENT_TEXT[accent])}>
                  {stat.icon}
                </div>
              )}
              <div className={cn("font-rajdhani text-4xl font-bold tracking-tight lg:text-5xl", ACCENT_TEXT[accent])}>
                {isNumeric ? <Counter value={numericValue} prefix={stat.prefix} suffix={stat.suffix} /> : `${stat.prefix ?? ""}${stat.value}${stat.suffix ?? ""}`}
              </div>
              <div className="mt-2 text-sm font-semibold text-foreground">{stat.label}</div>
              {stat.description && <p className="mt-1 text-xs leading-relaxed text-foreground/80/70">{stat.description}</p>}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
